import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublications } from '../../../fetchProjects';
import Image from '../../../components/AppImage';

const toSlug = (pub) => {
  const slug = (pub.title || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60)
    .replace(/-+$/, '');
  return `${pub.id}-${slug}`;
};

const ResearchPublications = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef(null);
  const SHOW_COUNT = 5;

  useEffect(() => {
    fetchPublications()
      .then(data => { setPublications(data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleToggle = () => {
    if (showAll && sectionRef.current) {
      const y = sectionRef.current.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setShowAll(s => !s);
  };

  const visible = showAll ? publications : publications.slice(0, SHOW_COUNT);

  if (loading) {
    return (
      <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 shadow-2xl">
        <div className="h-8 w-64 bg-white/10 rounded-lg animate-pulse mb-6" />
        <div className="space-y-5">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}
        </div>
      </section>
    );
  }

  if (!publications.length) return null;

  return (
    <section ref={sectionRef} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30" />

      <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary mb-6 relative z-10">
        Research &amp; Publications
      </h2>

      <div className="space-y-6 relative z-10">
        {visible.map(pub => (
          <div key={pub.id} className="bg-white/5 border border-white/10 rounded-lg p-5 group hover:border-accent/30 transition-smooth">
            <div className="flex items-start gap-4">

              {/* Logo / Cover Image — matches Education style */}
              {pub.image && (
                <div className="w-12 h-12 bg-white rounded flex items-center justify-center p-1 overflow-hidden shadow-sm flex-shrink-0">
                  <Image src={pub.image} alt={pub.title} className="w-full h-full object-contain" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                {/* Title — most prominent */}
                <h3 className="font-heading font-bold text-lg text-text-primary leading-snug mb-1 line-clamp-2">
                  {pub.title}
                </h3>

                {/* Authors */}
                <p className="text-accent font-semibold text-sm mb-1">{pub.authors}</p>

                {/* Journal · Year · Type · Status — subtle, inline */}
                <p className="text-text-secondary text-sm">
                  {[pub.journal, pub.year, pub.publicationType, pub.status].filter(Boolean).join(' · ')}
                </p>
              </div>

              {/* Read More — right-aligned, matches WorkExperience link style */}
            </div>

            {/* Read More */}
            <div className="mt-4 pt-3 border-t border-white/5">
              <Link
                to={`/publication/${toSlug(pub)}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent-light transition-smooth group/link"
              >
                Read More
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover/link:translate-x-1 transition-transform"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </Link>
            </div>
          </div>
        ))}
      </div>

      {publications.length > SHOW_COUNT && (
        <div className="mt-8 pt-6 border-t border-white/10 flex justify-center relative z-10">
          <button
            onClick={handleToggle}
            className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-6 py-2 text-accent hover:text-accent-light text-sm font-semibold flex items-center gap-2 transition-all duration-300"
          >
            {showAll ? 'Show Less' : `Read More (${publications.length - SHOW_COUNT} More)`}
          </button>
        </div>
      )}
    </section>
  );
};

export default ResearchPublications;
